import {
  Alert,
  AlertDescription,
  UnorderedList,
  ListItem,
  Box,
  Heading,
  Collapse,
} from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { consentError, generalError, medicalError } from "./validation";

// Map error codes to translation keys
const errorKeyMap: Record<generalError | consentError | medicalError, string> = {
  applicant_type_missing: "applicantTypeMissing",
  proxy_country_missing: "proxyCountryMissing",
  proxy_state_missing: "proxyStateMissing",
  proxy_date_of_birth_missing: "proxyDateOfBirthMissing",
  proxy_not_adult: "proxyNotAdult",
  proxy_first_name_missing: "proxyFirstNameMissing",
  proxy_last_name_missing: "proxyLastNameMissing",
  proxy_phone_missing: "proxyPhoneMissing",
  patient_first_name_missing: "patientFirstNameMissing",
  patient_last_name_missing: "patientLastNameMissing",
  patient_date_of_birth_missing: "patientDateOfBirthMissing",
  patient_not_adult: "patientNotAdult",
  patient_country_missing: "patientCountryMissing",
  patient_state_missing: "patientStateMissing",
  patient_phone_missing: "patientPhoneMissing",
  patient_email_malformed: "patientEmailMalformed",
  travel_willing_missing: "travelWillingMissing",
  health_collection_consent_missing: "healthCollectionConsentMissing",
  contact_consent_missing: "contactConsentMissing",
  contact_email_consent_missing: "contactEmailConsentMissing",
  health_sharing_consent_missing: "healthSharingConsentMissing",
  vision_conditions_missing: "visionConditionsMissing",
  vision_macular_degeneration_type_missing: "visionMacularDegenerationTypeMissing",
  vision_other_details_missing: "visionOtherDetailsMissing",
  vision_acuity_known_missing: "visionAcuityKnownMissing",
  vision_acuity_left_missing: "visionAcuityLeftMissing",
  vision_acuity_right_missing: "visionAcuityRightMissing",
  vision_acuity_left_not_numeric: "visionAcuityLeftNotNumeric",
  vision_acuity_right_not_numeric: "visionAcuityRightNotNumeric",
  vision_can_read_normal_print_missing: "visionCanReadNormalPrintMissing",
  vision_can_read_headlines_missing: "visionCanReadHeadlinesMissing",
  vision_can_see_indoors_missing: "visionCanSeeIndoorsMissing",
  vision_can_localize_person_missing: "visionCanLocalizePersonMissing",
  vision_driving_missing: "visionDrivingMissing",
};

const ErrorSummary = ({
  errors,
  extraMessages,
}: {
  errors: (generalError | consentError | medicalError)[];
  extraMessages?: string[];
}) => {
  const { t } = useTranslation("validation");

  const heading =
    errors.length > 1 ? t("errorSummary.headingPlural") : t("errorSummary.heading") + ":";

  return (
    <Collapse in={errors.length > 0}>
      <Box maxWidth="container.md" marginInline="auto">
        <Alert
          status="info"
          backgroundColor="transparent"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
          padding={6}
        >
          <AlertDescription>
            <Heading fontSize="xl" fontWeight="medium" marginBlockEnd={4}>
              {heading}
            </Heading>
            <UnorderedList paddingInlineStart={2}>
              {extraMessages?.map((message) => (
                <ListItem fontSize="lg" key={message}>
                  {message}
                </ListItem>
              ))}
              {errors.map((error) => (
                <ListItem fontSize="lg" key={error}>
                  {t(`errorSummary.${errorKeyMap[error]}`)}
                </ListItem>
              ))}
            </UnorderedList>
          </AlertDescription>
        </Alert>
      </Box>
    </Collapse>
  );
};

export default ErrorSummary;
